import type { NonCashOrderPayment, ShopCashSettlementDto } from 'apis/fms';
import type { CashPaymentSummaryItem } from 'apis/oms';
import type {
  CashBreakdown,
  FmsAccountManagement,
  FmsRecycleStat,
} from 'types/fms';

type SaleCashPayment = {
  payAmount: string | number;
};

type RecycledConsignmentCashPayment = {
  totalAmount: string | number;
};

type RefundCashPayment = {
  payType: string;
  payAmount: string | number;
};

const isCashPayType = (
  payType: string,
  paymentAccountList: FmsAccountManagement[],
): boolean => {
  const account = paymentAccountList.find(
    (item) => item.accountCode === payType,
  );

  return account?.isCash === 1;
};

type CalculateCashSettlementAmountsParams = {
  beginningCash: number;
  actualCash: number;
  saleCashPayments: SaleCashPayment[];
  recycledConsignmentCashPayments: RecycledConsignmentCashPayment[];
  treasuryCashInAmount: number;
  treasuryCashOutAmount: number;
  refundPayments: RefundCashPayment[];
  paymentAccountList: FmsAccountManagement[];
};

const roundAmount = (amount: number): number => Math.round(amount * 100) / 100;

const sumAmounts = <T>(
  records: T[],
  getAmount: (record: T) => string | number,
): number => {
  return roundAmount(
    records.reduce(
      (total: number, record) => total + Number(getAmount(record) || 0),
      0,
    ),
  );
};

export const calculateCashSettlementAmounts = ({
  beginningCash,
  actualCash,
  saleCashPayments,
  recycledConsignmentCashPayments,
  treasuryCashInAmount,
  treasuryCashOutAmount,
  refundPayments,
  paymentAccountList,
}: CalculateCashSettlementAmountsParams) => {
  const saleCashTotal = sumAmounts(
    saleCashPayments,
    (payment) => payment.payAmount,
  );
  const recycledConsignmentCashTotal = sumAmounts(
    recycledConsignmentCashPayments,
    (payment) => payment.totalAmount,
  );
  const cashRefundTotal = sumAmounts(
    refundPayments.filter((payment) =>
      isCashPayType(payment.payType, paymentAccountList),
    ),
    (payment) => payment.payAmount,
  );
  const systemCashSales = roundAmount(
    saleCashTotal - recycledConsignmentCashTotal - cashRefundTotal,
  );
  const treasuryCashMovementNet = roundAmount(
    treasuryCashInAmount - treasuryCashOutAmount,
  );
  const expectedCash = roundAmount(
    beginningCash + systemCashSales + treasuryCashMovementNet,
  );
  const variance = roundAmount(actualCash - expectedCash);

  return {
    saleCashTotal,
    recycledConsignmentCashTotal,
    cashRefundTotal,
    systemCashSales,
    treasuryCashMovementNet,
    expectedCash,
    variance,
  };
};

type CashSettlementPayloadCashData = {
  beginningCash: number;
  treasuryCashInAmount: number;
  treasuryCashOutAmount: number;
  actualCash: number;
  note: string;
  cashCounter: string;
  cashBreakdown: CashBreakdown;
};

type BuildCashSettlementPayloadParams = {
  shopId: number;
  cashData: CashSettlementPayloadCashData;
  saleCashPayments: NonCashOrderPayment[];
  recycledConsignmentCashPayments: CashPaymentSummaryItem[];
  nonCashPayments: NonCashOrderPayment[];
  recycleStats: FmsRecycleStat[];
  refundOrderPayments: NonCashOrderPayment[];
  paymentAccountList: FmsAccountManagement[];
  includeCashBreakdown: boolean;
};

export const normalizeCashBreakdownCount = (count: number | null): number => {
  return count === null ? 0 : Math.max(0, Math.floor(count));
};

export const calculateCashBreakdownTotal = (
  cashBreakdown: CashBreakdown,
): number => {
  const total = (['bills', 'coins'] as const).reduce((sum, type) => {
    const entries = Object.entries(cashBreakdown[type] ?? {});

    return (
      sum +
      entries.reduce((subtotal, [denomination, count]) => {
        return subtotal + parseFloat(denomination) * count;
      }, 0)
    );
  }, 0);

  return roundAmount(total);
};

export const buildCashSettlementPayload = ({
  shopId,
  cashData,
  saleCashPayments,
  recycledConsignmentCashPayments,
  nonCashPayments,
  recycleStats,
  refundOrderPayments,
  paymentAccountList,
  includeCashBreakdown,
}: BuildCashSettlementPayloadParams): ShopCashSettlementDto => {
  const { systemCashSales, expectedCash, variance } =
    calculateCashSettlementAmounts({
      beginningCash: cashData.beginningCash,
      actualCash: cashData.actualCash,
      saleCashPayments,
      recycledConsignmentCashPayments,
      treasuryCashInAmount: cashData.treasuryCashInAmount,
      treasuryCashOutAmount: cashData.treasuryCashOutAmount,
      refundPayments: refundOrderPayments,
      paymentAccountList,
    });
  const nonCashTotal = nonCashPayments.reduce(
    (total: number, payment: NonCashOrderPayment) =>
      total + Number(payment.payAmount),
    0,
  );

  return {
    shopId,
    beginningCashAmount: cashData.beginningCash,
    treasuryCashInAmount: cashData.treasuryCashInAmount,
    treasuryCashOutAmount: cashData.treasuryCashOutAmount,
    systemCashSales,
    expectedCash,
    verifiedCashAmount: cashData.actualCash,
    varianceAmount: variance,
    cashCounter: cashData.cashCounter,
    note: cashData.note,
    cashBreakdown: includeCashBreakdown
      ? cashData.cashBreakdown
      : { bills: {}, coins: {} },
    nonCashTotal,
    paymentDetails: {
      cashPayments: saleCashPayments,
      nonCashPayments,
      refundPayments: refundOrderPayments,
    },
    recycleStats,
  };
};
